import { Box, Typography } from '@mui/material'
import CommentCountIcon from 'components/ui/comment-count-icon'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { GridCommentCellProps } from './types'

/**
 * Builds a comment route based on the row data and config
 */
const buildCommentRoute = (
  row: any,
  routeConfig: GridCommentCellProps['routeConfig']
): string => {
  if (!routeConfig) return ''

  const { basePath, appendComments = true, childQueryParam } = routeConfig
  const { PK, SK, isParent } = row ?? {}

  const commentsPath = appendComments ? '/comments' : ''

  if (isParent) {
    return `${basePath}/${PK}${commentsPath}`
  }

  if (childQueryParam && SK) {
    return `${basePath}/${PK}${commentsPath}?${childQueryParam}=${SK}`
  }

  return `${basePath}/${PK}${commentsPath}`
}

/**
 * A reusable comment cell component for DataGrid columns.
 * Displays a comment count icon and handles navigation to comments.
 *
 * @example
 * // In column definition for MTB grid
 * {
 *   field: 'commentCount',
 *   renderCell: (params) => (
 *     <GridCommentCell
 *       params={params}
 *       densityStyles={densityStyles}
 *       boardTheme="icon.mtb"
 *       routeConfig={{ basePath: '/mtb', childQueryParam: 'task' }}
 *       handleRowClick={handleRowClick}
 *     />
 *   )
 * }
 */
const GridCommentCell: FC<GridCommentCellProps> = ({
  params,
  densityStyles,
  boardTheme,
  route,
  routeConfig,
  onClick,
  handleRowClick,
  parentOnly = false,
  hidePinnedRows = false,
  countField,
}) => {
  const navigate = useNavigate()
  const { rowNode, row, value } = params ?? {}

  // Skip auto-generated rows
  // @ts-ignore
  if (rowNode?.isAutoGenerated) return null

  // Handle pinned rows
  const isPinnedRow = rowNode?.type === 'pinnedRow'
  if (hidePinnedRows && isPinnedRow) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Typography>–</Typography>
      </Box>
    )
  }

  // Handle parent-only display
  const { isParent } = row ?? {}
  if (parentOnly && !isParent) {
    return null
  }

  // Determine the comment count
  const commentCount = countField ? row?.[countField] : value

  // Build or use the route
  const commentRoute = route || buildCommentRoute(row, routeConfig)

  // Handle click
  const handleClick = () => {
    if (onClick) {
      onClick(params)
    } else if (handleRowClick) {
      handleRowClick(params?.row, '/comments')
    } else if (commentRoute) {
      navigate(commentRoute)
    }
  }

  return (
    <Box
      key={row?.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: parentOnly ? 'left' : 'center',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        ':hover': {
          cursor: 'pointer',
        },
      }}
      onClick={handleClick}
    >
      <CommentCountIcon
        commentCount={commentCount ?? 0}
        boardTheme={boardTheme}
        densityStyles={densityStyles}
      />
    </Box>
  )
}

export default GridCommentCell
